import { useState, useRef, useEffect, FC } from 'react';
import { motion } from 'motion/react';
import styled from 'styled-components';
import { Caption } from '@comeon/ui';

const getContentVariants = (overflowHeight = '0') => ({
	open: { height: 'auto' },
	collapsed: { height: `${overflowHeight}px` }
});

const Wrapper = styled.div`
	position: relative;
`;

const ToggleCTA = styled.button`
	position: absolute;
	bottom: 0;
	left: 0;
	outline: 0;
	> * {
		color: var(--custom-show-more-btn-color, rgba(var(--secondary), 1));
	}
`;
const MotionContentWrapper = styled(motion.div)`
	overflow: hidden;
`;

type Props = {
	showLessLabel: string;
	showMoreLabel: string;
	overflowHeight: string;
	children?: React.ReactNode;
};

const CollapseOnOverflow: FC<Props> = ({ showLessLabel, showMoreLabel, children, overflowHeight }) => {
	const [collapse, setCollapse] = useState(true);
	const [overflow, setOverflow] = useState(false);
	const contentEl = useRef<HTMLDivElement>(null);
	// run only on component mount
	useEffect(() => {
		// check if text overflows
		if (contentEl?.current?.children[0] && Number(overflowHeight) < contentEl.current.children[0].scrollHeight) {
			setOverflow(true);
		}
	}, [overflowHeight]);
	const toggleCollapse = () => {
		setCollapse(!collapse);
	};
	const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.stopPropagation();
		e.preventDefault();
		toggleCollapse();
	};
	const contentVariants = getContentVariants(overflowHeight);
	return overflow ? (
		<Wrapper className="u-pb2">
			<MotionContentWrapper
				ref={contentEl}
				initial="collapsed"
				variants={contentVariants}
				animate={collapse ? 'collapsed' : 'open'}
				transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
			>
				{children}
			</MotionContentWrapper>
			<ToggleCTA onClick={handleClick}>
				<Caption>{collapse ? showMoreLabel : showLessLabel}</Caption>
			</ToggleCTA>
		</Wrapper>
	) : (
		<Wrapper className="u-pb2" ref={contentEl}>
			{children}
		</Wrapper>
	);
};

export default CollapseOnOverflow;
